import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet-imageoverlay-rotated";
import { toast } from "sonner";
import {
  Home,
  Layers,
  Eye,
  EyeOff,
  MapPin,
  Loader2,
  Move,
  RotateCcw,
  RotateCw,
  Plus,
  Minus,
  Save,
  Undo2,
  SlidersHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";

const OVERLAY_ID = "ssm_schlossplatz";
const DEG = Math.PI / 180;
const STAND_CATS = ["warenverkauf", "koch", "gastro", "fahrgeschaeft"];

const centerOf = (c) => [(c.tr[0] + c.bl[0]) / 2, (c.tr[1] + c.bl[1]) / 2];

// move (dE/dN meters), rotate (deg), scale (factor) about the overlay centre
function editCorners(c, { dE = 0, dN = 0, rotDeg = 0, scale = 1 }) {
  const ctr = centerOf(c);
  const mlat = 111320;
  const mlng = 111320 * Math.cos(ctr[0] * DEG);
  const th = rotDeg * DEG;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const tf = (p) => {
    let E = (p[1] - ctr[1]) * mlng;
    let N = (p[0] - ctr[0]) * mlat;
    E *= scale;
    N *= scale;
    const E2 = cos * E - sin * N;
    const N2 = sin * E + cos * N;
    return [ctr[0] + (N2 + dN) / mlat, ctr[1] + (E2 + dE) / mlng];
  };
  return { tl: tf(c.tl), tr: tf(c.tr), bl: tf(c.bl) };
}

// map a PDF point to lat/lng through the current 3 corners (affine parallelogram)
function symPos(c, x, y, W, H) {
  const u = x / W;
  const v = y / H;
  return [
    c.tl[0] + u * (c.tr[0] - c.tl[0]) + v * (c.bl[0] - c.tl[0]),
    c.tl[1] + u * (c.tr[1] - c.tl[1]) + v * (c.bl[1] - c.tl[1]),
  ];
}

const STEPS = [0.1, 0.5, 1, 2, 5];

export default function TestMap() {
  const navigate = useNavigate();
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const layerRef = useRef(null);
  const didFit = useRef(false);

  const [overlay, setOverlay] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [opacity, setOpacity] = useState(0.7);
  const [hidden, setHidden] = useState(() => new Set());

  const [corners, setCorners] = useState(null);
  const [saved, setSaved] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [step, setStep] = useState(0.5);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const pdf = useMemo(
    () => ({ W: overlay?.pdf_w || 842, H: overlay?.pdf_h || 1191 }),
    [overlay]
  );

  useEffect(() => {
    document.title = "Testkarte · Wiesbaden SSM";
  }, []);

  // init map once
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true }).setView([50.0822, 8.2419], 17);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // load data
  useEffect(() => {
    let ok = true;
    Promise.all([api.getTestOverlays(), api.getTestSymbols(OVERLAY_ID)])
      .then(([ov, sy]) => {
        if (!ok) return;
        const o = (ov.data.overlays || []).find((x) => x.id === OVERLAY_ID) || null;
        setOverlay(o);
        setSymbols(sy.data.symbols || []);
        if (o) {
          const c = { tl: o.corner_tl, tr: o.corner_tr, bl: o.corner_bl };
          setCorners(c);
          setSaved(c);
        }
      })
      .finally(() => ok && setLoading(false));
    return () => {
      ok = false;
    };
  }, []);

  // create overlay + fit once
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !overlay || !corners) return;
    if (!overlayRef.current) {
      const url = api.testOverlayImageUrl(overlay.id);
      overlayRef.current = L.imageOverlay.rotated(
        url,
        L.latLng(corners.tl),
        L.latLng(corners.tr),
        L.latLng(corners.bl),
        { opacity, interactive: false }
      );
      if (showOverlay) overlayRef.current.addTo(map);
    }
    if (!didFit.current) {
      didFit.current = true;
      const tl = L.latLng(corners.tl);
      const tr = L.latLng(corners.tr);
      const bl = L.latLng(corners.bl);
      const br = L.latLng(tr.lat + (bl.lat - tl.lat), tr.lng + (bl.lng - tl.lng));
      map.fitBounds(L.latLngBounds([tl, tr, bl, br]).pad(0.08));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, corners]);

  // reposition overlay whenever corners change
  useEffect(() => {
    if (!overlayRef.current || !corners) return;
    overlayRef.current.reposition(
      L.latLng(corners.tl),
      L.latLng(corners.tr),
      L.latLng(corners.bl)
    );
  }, [corners]);

  // overlay visibility / opacity
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !overlayRef.current) return;
    if (showOverlay) {
      overlayRef.current.addTo(map);
      overlayRef.current.setOpacity(opacity);
    } else {
      overlayRef.current.remove();
    }
  }, [showOverlay, opacity]);

  // draw markers from current corners + pdf coords
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !corners) return;
    layer.clearLayers();
    symbols.forEach((s) => {
      if (hidden.has(s.category)) return;
      const [lat, lng] =
        s.pdf_x != null ? symPos(corners, s.pdf_x, s.pdf_y, pdf.W, pdf.H) : [s.lat, s.lng];
      const isStand = STAND_CATS.includes(s.category);
      const marker = L.circleMarker([lat, lng], {
        radius: isStand ? 5 : 7,
        color: "#ffffff",
        weight: isStand ? 1.5 : 2,
        fillColor: s.color,
        fillOpacity: 0.95,
      });
      marker.bindPopup(
        `<div style="font-family:Manrope,sans-serif">
           <div style="font-weight:800;font-size:13px">${s.label || s.categoryName}</div>
           <div style="font-size:11px;color:#64748b">${s.categoryName}</div>
         </div>`
      );
      marker.addTo(layer);
    });
  }, [symbols, hidden, corners, pdf]);

  const apply = useCallback((opt) => {
    setCorners((c) => editCorners(c, opt));
    setDirty(true);
  }, []);

  const categories = useMemo(() => {
    const m = {};
    symbols.forEach((s) => {
      if (!m[s.category])
        m[s.category] = { key: s.category, name: s.categoryName || s.category, color: s.color, count: 0 };
      m[s.category].count += 1;
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [symbols]);

  const toggleCat = (key) =>
    setHidden((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const visibleCount = symbols.filter((s) => !hidden.has(s.category)).length;

  const enterEdit = () => {
    setEditMode(true);
    setShowOverlay(true);
    if (opacity < 0.45) setOpacity(0.6);
  };

  const doSave = async () => {
    if (!corners) return;
    setSaving(true);
    try {
      await api.saveTestOverlayCorners(OVERLAY_ID, {
        corner_tl: corners.tl,
        corner_tr: corners.tr,
        corner_bl: corners.bl,
      });
      setSaved(corners);
      setDirty(false);
      toast.success("Position gespeichert – wird jetzt immer übernommen");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const resetToSaved = () => {
    setCorners(saved);
    setDirty(false);
  };

  const StepBtn = ({ onClick, testid, children, title }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className="grid h-10 w-10 place-items-center rounded-xl bg-white text-slate-700 shadow ring-1 ring-slate-200 transition-transform hover:scale-105 active:scale-95"
    >
      {children}
    </button>
  );

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-slate-100" data-testid="test-map-page">
      <div ref={elRef} className="absolute inset-0 z-0" data-testid="test-map-container" />

      {/* Title */}
      <div className="glass pointer-events-auto absolute left-3 top-3 z-[500] max-w-[70vw] rounded-2xl px-4 py-2.5">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
          <MapPin size={12} /> Testkarte · Sandbox
        </p>
        <h1 className="truncate font-display text-base font-bold leading-tight text-slate-900">
          Wiesbaden – Sternschnuppenmarkt
        </h1>
        <p className="text-xs text-slate-500">
          {loading ? "lädt…" : `${visibleCount} / ${symbols.length} Symbole`}
          {editMode && dirty && (
            <span className="ml-2 font-bold text-amber-600" data-testid="edit-dirty">
              • ungespeichert
            </span>
          )}
        </p>
      </div>

      {/* Top-right buttons */}
      <div className="absolute right-3 top-3 z-[500] flex items-center gap-2">
        <button
          onClick={editMode ? () => setEditMode(false) : enterEdit}
          data-testid="edit-mode-toggle"
          className={`flex h-11 items-center gap-2 rounded-2xl px-3.5 text-sm font-bold shadow transition-transform hover:scale-105 active:scale-95 ${
            editMode ? "bg-primary text-white" : "glass text-slate-700"
          }`}
        >
          <Move size={16} /> {editMode ? "Edit beenden" : "Edit-Modus"}
        </button>
        <button
          onClick={() => navigate("/")}
          data-testid="test-home-link"
          aria-label="Startseite"
          className="glass grid h-11 w-11 place-items-center rounded-2xl text-slate-600 transition-transform hover:scale-105 active:scale-95"
        >
          <Home size={18} />
        </button>
      </div>

      {loading && (
        <div className="pointer-events-none absolute inset-0 z-[400] grid place-items-center">
          <Loader2 className="animate-spin text-primary" size={30} />
        </div>
      )}

      {/* EDIT PANEL */}
      {editMode ? (
        <div className="glass absolute bottom-3 right-3 z-[500] w-72 max-w-[88vw] rounded-2xl p-4" data-testid="edit-panel">
          <div className="mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <SlidersHorizontal size={16} /> Overlay positionieren
            </span>
            <button
              onClick={() => setShowOverlay((v) => !v)}
              data-testid="overlay-toggle"
              className={`grid h-8 w-8 place-items-center rounded-lg ${
                showOverlay ? "bg-primary text-white" : "bg-slate-200 text-slate-600"
              }`}
              title="Overlay ein/aus"
            >
              {showOverlay ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
          </div>

          {/* Step selector */}
          <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Schrittweite
          </p>
          <div className="mb-3 flex gap-1.5">
            {STEPS.map((v) => (
              <button
                key={v}
                onClick={() => setStep(v)}
                data-testid={`edit-step-${v}`}
                className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-colors ${
                  step === v ? "bg-primary text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <p className="mb-3 text-[10px] text-slate-400">
            Bewegen {step} m · Drehen {step}° · Größe {step}%
          </p>

          {/* Controls grid */}
          <div className="flex items-start justify-between gap-2">
            {/* Move pad */}
            <div className="grid grid-cols-3 gap-1.5">
              <div />
              <StepBtn onClick={() => apply({ dN: step })} testid="edit-move-n" title="Nach Norden">
                <ArrowUp size={18} />
              </StepBtn>
              <div />
              <StepBtn onClick={() => apply({ dE: -step })} testid="edit-move-w" title="Nach Westen">
                <ArrowLeft size={18} />
              </StepBtn>
              <div className="grid h-10 w-10 place-items-center text-slate-300">
                <Move size={16} />
              </div>
              <StepBtn onClick={() => apply({ dE: step })} testid="edit-move-e" title="Nach Osten">
                <ArrowRight size={18} />
              </StepBtn>
              <div />
              <StepBtn onClick={() => apply({ dN: -step })} testid="edit-move-s" title="Nach Süden">
                <ArrowDown size={18} />
              </StepBtn>
              <div />
            </div>

            {/* Rotate + scale */}
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-1.5">
                <StepBtn onClick={() => apply({ rotDeg: -step })} testid="edit-rotate-left" title="Gegen Uhrzeigersinn">
                  <RotateCcw size={18} />
                </StepBtn>
                <StepBtn onClick={() => apply({ rotDeg: step })} testid="edit-rotate-right" title="Im Uhrzeigersinn">
                  <RotateCw size={18} />
                </StepBtn>
              </div>
              <div className="flex gap-1.5">
                <StepBtn onClick={() => apply({ scale: 1 + step / 100 })} testid="edit-scale-plus" title="Vergrößern">
                  <Plus size={18} />
                </StepBtn>
                <StepBtn onClick={() => apply({ scale: 1 - step / 100 })} testid="edit-scale-minus" title="Verkleinern">
                  <Minus size={18} />
                </StepBtn>
              </div>
            </div>
          </div>

          {/* Opacity */}
          <div className="mt-3">
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              data-testid="overlay-opacity"
              className="w-full accent-amber-600"
            />
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              Deckkraft {Math.round(opacity * 100)}%
            </p>
          </div>

          {/* Save / reset */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={doSave}
              disabled={saving || !dirty}
              data-testid="edit-save"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-white shadow transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Speichern
            </button>
            <button
              onClick={resetToSaved}
              disabled={!dirty}
              data-testid="edit-reset"
              className="flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-bold text-slate-600 ring-1 ring-slate-200 disabled:opacity-40"
              title="Auf gespeicherten Stand zurück"
            >
              <Undo2 size={16} />
            </button>
          </div>
        </div>
      ) : (
        /* NORMAL PANEL */
        <div className="glass absolute bottom-3 right-3 z-[500] w-72 max-w-[85vw] rounded-2xl p-4" data-testid="test-controls">
          <div className="mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <Layers size={16} /> PDF-Overlay
            </span>
            <button
              onClick={() => setShowOverlay((v) => !v)}
              data-testid="overlay-toggle"
              className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-colors ${
                showOverlay ? "bg-primary text-white" : "bg-slate-200 text-slate-600"
              }`}
            >
              {showOverlay ? <Eye size={14} /> : <EyeOff size={14} />}
              {showOverlay ? "An" : "Aus"}
            </button>
          </div>
          <div className="mb-3">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              disabled={!showOverlay}
              data-testid="overlay-opacity"
              className="w-full accent-amber-600 disabled:opacity-40"
            />
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              Deckkraft {Math.round(opacity * 100)}%
            </p>
          </div>
          <div className="max-h-56 overflow-y-auto border-t border-slate-200 pt-2">
            {categories.map((c) => {
              const off = hidden.has(c.key);
              return (
                <button
                  key={c.key}
                  onClick={() => toggleCat(c.key)}
                  data-testid={`cat-toggle-${c.key}`}
                  className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-sm transition-opacity hover:bg-slate-50 ${
                    off ? "opacity-40" : ""
                  }`}
                >
                  <span
                    className="h-3 w-3 shrink-0 rounded-full border border-white shadow"
                    style={{ background: c.color }}
                  />
                  <span className="flex-1 truncate text-slate-700">{c.name}</span>
                  <span className="text-xs font-bold text-slate-400">{c.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
